import secureLocalStorage from "react-secure-storage";
import algosdk from "algosdk";

import { findInObjectArrayByMatchingProperties } from "helpers/functions";

import * as api from "middlewares/apis";
import dexieDB from "database/dexieDB";
import { decryptUint8Array } from "middlewares/cryptoJs";

const token = { "x-api-key": process.env.REACT_APP_PURESTAKE_API_KEY };

//	initialize alammex client for mainnet & testnet
const mainnetUri = process.env.REACT_APP_ALGOD_MAINNET_BASE_URL;
const testnetUri = process.env.REACT_APP_ALGOD_TESTNET_BASE_URL;

const algodMainnetClient = new algosdk.Algodv2(token, mainnetUri, "");
const algodTestnetClient = new algosdk.Algodv2(token, testnetUri, "");

// Algo Transactions
export const sendAlgosToAccount = async (formData, algoChain) => {
	let algodClient = algoChain === "mainnet" ? algodMainnetClient : algodTestnetClient;

	if (formData?.["sender-account"] && formData?.["sending-amount"] && formData?.["receiver-account"]) {
		try {
			//Check your balance
			// let accountInfo = await algodClient.accountInformation(formData["sender-account"]).do();
			// console.log("Account balance: %d microAlgos", accountInfo.amount);

			// Construct the transaction
			let params = await algodClient.getTransactionParams().do();
			// comment out the next two lines to use suggested fee
			params.fee = algosdk.ALGORAND_MIN_TX_FEE;
			params.flatFee = true;

			const enc = new TextEncoder();

			let txn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
				from: formData["sender-account"],
				to: formData["receiver-account"],
				amount: formData["sending-amount"],
				note: formData?.note ? enc.encode(formData.note) : enc.encode("DaffiWallet"),
				suggestedParams: params,
			});

			const secretKey = await getSecretKeyByAccountID(formData["sender-account"]);

			if (secretKey) {
				// Sign the transaction
				let signedTxn = txn.signTxn(secretKey);
				let txId = txn.txID().toString();
				// console.log("Signed transaction with txID: %s", txId);

				// Submit the transaction
				await algodClient.sendRawTransaction(signedTxn).do();

				// Wait for confirmation
				let confirmedTxn = await algosdk.waitForConfirmation(algodClient, txId, 4);
				//Get the completed Transaction
				// console.log("Transaction " + txId + " confirmed in round " + confirmedTxn["confirmed-round"]);

				return { txID: txId, "txn-amount": confirmedTxn.txn.txn.amt };
			} else console.log("secret key not found in local storage");
		} catch (error) {
			console.error(error);
		}
	}
};

export const sendAssetToAccount = async (formData, algoChain) => {
	let algodClient = algoChain === "mainnet" ? algodMainnetClient : algodTestnetClient;

	if (formData?.["asset-id"] && algodClient) {
		try {
			let params = await algodClient.getTransactionParams().do();
			const enc = new TextEncoder();

			const transactionOptions = {
				from: formData["sender-account"],
				to: formData["receiver-account"],
				note: formData?.note ? enc.encode(formData.note) : enc.encode("DaffiWallet"),
				amount: formData?.["sending-amount"],
				assetIndex: parseInt(formData["asset-id"]),
				suggestedParams: params,
			};

			const txn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject(transactionOptions);

			const secretKey = await getSecretKeyByAccountID(formData["sender-account"]);

			if (secretKey) {
				const signedTxn = txn.signTxn(secretKey);

				let txId = txn.txID().toString();
				// console.log("Signed transaction with txID: %s", txId);

				await algodClient.sendRawTransaction(signedTxn).do();

				let confirmedTxn = await algosdk.waitForConfirmation(algodClient, txId, 4);
				// console.log("Transaction " + txId + " confirmed in round " + confirmedTxn["confirmed-round"]);

				return { txID: txId, "txn-amount": confirmedTxn.txn.txn.amt };
			} else console.log("secret key not found in local storage");
		} catch (error) {
			return { message: "Transaction UnSuccessful", error: error };
		}
	}
};

export const getFixedInputSwapQuote = async (formData, algoChain) => {
	formData.amount *= 1000000;

	try {
		const { data } = await api.getFixedInputSwapQuote(formData, algoChain);

		if (data?.quote) return data;
		else return { message: "Get quote UnSuccessful", error: true };
	} catch (error) {
		console.error(error);
		return { message: "Get quote UnSuccessful", error: error?.response?.data?.error };
	}
};

export const getFixedOutputSwapQuote = async (formData, algoChain) => {
	formData.amount *= 1000000;

	try {
		const { data } = await api.getFixedOutputSwapQuote(formData, algoChain);

		if (data?.quote) return data;
		else return { message: "Get quote UnSuccessful", error: true };
	} catch (error) {
		console.error(error);
		return { message: "Get quote UnSuccessful", error: error?.response?.data?.error };
	}
};

export const getSwapQuoteTransactions = async (formData, algoChain) => {
	let tempTxns = [];

	if (formData) {
		// formData.referrer = "R4DV6MVXAUGKLHMUCNQRH2FAAH4OVKZFVES3TSUUR25XMKDAZ4XXIEDXEI";

		const { data } = await api.executeSwapTxns(formData, algoChain);

		if (data?.txns) {
			for (let txn of data.txns) {
				if (txn?.logicSigBlob !== false) {
					tempTxns.push({ ...txn, logicSigBlob: new Uint8Array(Object.values(txn.logicSigBlob)) });
				} else tempTxns.push(txn);
			}

			data.txns = tempTxns;

			return data;
		} else return { message: "Get swap transactions UnSuccessful", error: true };
	}
};

export const sendSwapQuoteTransactions = async (formData, algoChain) => {
	let algodClient = algoChain === "mainnet" ? algodMainnetClient : algodTestnetClient;

	try {
		const secretKey = await getSecretKeyByAccountID(formData["sender-account"]);

		if (secretKey) {
			const signedTxns = formData.txnGroup.txns.map((txn) => {
				if (txn.logicSigBlob !== false) {
					return txn.logicSigBlob;
				} else {
					let bytes = new Uint8Array(Buffer.from(txn.data, "base64"));
					const decoded = algosdk.decodeUnsignedTransaction(bytes);
					return algosdk.signTransaction(decoded, secretKey).blob;
				}
			});

			const { txId } = await algodClient.sendRawTransaction(signedTxns).do();
			console.log("Signed transaction with txID: %s", txId);

			let confirmedTxn = await algosdk.waitForConfirmation(algodClient, txId, 4);
			console.log("Transaction " + txId + " confirmed in round " + confirmedTxn["confirmed-round"]);

			return { txID: txId, "txn-amount": confirmedTxn.txn.txn.amt };
		}
	} catch (error) {
		console.error(error);
		return { message: "Transaction UnSuccessful", error: error };
	}
};

// export const fundAlgoInNewAccount = async (formData, algoChain) => {
// 	// try {
// 	// 	const { data } = await api.fundAlgoInNewAccount(formData, algoChain);
// 	// 	return data;
// 	// } catch (error) {
// 	// 	console.error(error);
// 	// }
// };

// export const fundAssetInNewAccount = async (formData, algoChain) => {
// 	// try {
// 	// 	const { data } = await api.fundAssetInNewAccount(formData, algoChain);
// 	// 	return data;
// 	// } catch (error) {
// 	// 	console.error(error);
// 	// }
// };

export const optInAssetToAccount = async (formData, algoChain) => {
	if (formData?.["account-address"] && formData?.["asset-id"]) {
		try {
			const algodClient = algoChain === "mainnet" ? algodMainnetClient : algodTestnetClient;

			const secretKey = await getSecretKeyByAccountID(formData["account-address"]);

			if (secretKey) {
				const params = await algodClient.getTransactionParams().do();

				// Opting in to an Asset:
				const amount = 0;
				const sender = formData?.["account-address"];
				const recipient = formData?.["account-address"];
				const assetID = formData?.["asset-id"];
				const revocationTarget = undefined;
				const closeRemainderTo = undefined;
				const note = undefined;

				// signing and sending "txn" allows sender to begin accepting asset specified by creator and index
				const opttxn = algosdk.makeAssetTransferTxnWithSuggestedParams(
					sender,
					recipient,
					closeRemainderTo,
					revocationTarget,
					amount,
					note,
					assetID,
					params
				);

				// Must be signed by the account wishing to opt in to the asset
				const rawSignedTxn = opttxn.signTxn(secretKey);

				if (rawSignedTxn) {
					const opttx = await algodClient.sendRawTransaction(rawSignedTxn).do();
					console.log(`Transaction ID: ${opttx.txId}.\nNow waiting for transaction confirmation...`);

					// wait for transaction to be confirmed
					const confirmedTxn = await algosdk.waitForConfirmation(algodClient, opttx.txId, 4);
					// console.log("Transaction " + txId + " confirmed in round " + confirmedTxn["confirmed-round"]);

					return { txID: opttx?.txId, "txn-amount": confirmedTxn?.txn?.txn };
				}
			} else console.log("secret key not found in local storage");
		} catch (error) {
			console.error(error);
		}
	}
};

export const optOutAssetFromAccount = async (formData, algoChain) => {
	if (formData?.["account-address"] && formData?.["asset-id"]) {
		try {
			const algodClient = algoChain === "mainnet" ? algodMainnetClient : algodTestnetClient;

			const secretKey = await getSecretKeyByAccountID(formData["account-address"]);

			if (secretKey) {
				const params = await algodClient.getTransactionParams().do();

				// Opting in to an Asset:
				const amount = 0;
				const sender = formData?.["account-address"];
				const recipient = formData?.["account-address"];
				const assetID = formData?.["asset-id"];
				const revocationTarget = undefined;
				const closeRemainderTo = formData?.creator;
				const note = undefined;

				// signing and sending "txn" allows sender to begin accepting asset specified by creator and index
				const opttxn = algosdk.makeAssetTransferTxnWithSuggestedParams(
					sender,
					recipient,
					closeRemainderTo,
					revocationTarget,
					amount,
					note,
					assetID,
					params
				);

				// Must be signed by the account wishing to opt in to the asset
				const rawSignedTxn = opttxn.signTxn(secretKey);

				if (rawSignedTxn) {
					const opttx = await algodClient.sendRawTransaction(rawSignedTxn).do();
					console.log(`Transaction ID: ${opttx.txId}.\nNow waiting for transaction confirmation...`);

					// wait for transaction to be confirmed
					const confirmedTxn = await algosdk.waitForConfirmation(algodClient, opttx.txId, 4);
					// console.log("Transaction " + txId + " confirmed in round " + confirmedTxn["confirmed-round"]);

					return { txID: opttx?.txId, "txn-amount": confirmedTxn?.txn?.txn };
				}
			} else console.log("secret key not found in local storage");
		} catch (error) {
			console.error(error);
		}
	}
};

const getSecretKeyByAccountID = async (accountID) => {
	const currentUser = secureLocalStorage.getItem("currentUser");

	const userData = await dexieDB.tAlgoSecretKeys
		.filter((item) => item?.userID === currentUser?._id)
		.first()
		.then((data) => {
			if (data) {
				return data;
			} else return null;
		});

	if (userData?.data) {
		const secretKeys = await findInObjectArrayByMatchingProperties(userData.data, {
			"account-address": accountID,
		});

		const decrypted = await decryptUint8Array(secretKeys[0]["secret-key"]);

		if (decrypted) return decrypted;
		else return null;
	} else return null;
};
